import { CommonModule } from '@angular/common';
import {
    Component,
    effect,
    input,
    InputSignal,
    output,
    OutputEmitterRef,
    Signal,
    viewChild,
} from '@angular/core';
import { IqEventBusNode } from '@dat/iq-event-bus';
import {
    type CellStyle,
    Cell,
    EventObject,
    Graph,
    HierarchicalLayout,
    InternalEvent,
    Morphing,
} from '@maxgraph/core';
import { debounce, startCase } from 'lodash';

type EventTraceLink = {
    targetEvent: Partial<IqEventBusNode>;
    sourceEvent: Partial<IqEventBusNode>;
    meta: any;
};

type EventTraceNode = {
    id: string;
    info: Partial<IqEventBusNode>;
};

export type EventTraceMap = {
    nodes: EventTraceNode[];
    links: EventTraceLink[];
};

enum DIRECTION {
    NORTH = 'north',
    SOUTH = 'south',
    EAST = 'east',
    WEST = 'west',
}

@Component({
    selector: 'iq-builder-events-trace-diagram',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './events-trace-diagram.component.html',
    styleUrl: './events-trace-diagram.component.scss',
})
export class EventsTraceDiagramComponent {
    diagramContainer: Signal<any> = viewChild<any>('diagramContainer');
    graph: Graph;
    traceMap: InputSignal<EventTraceMap> = input<EventTraceMap>();
    selected: OutputEmitterRef<string> = output<string>();

    debounceUpdate = debounce((traceMap: EventTraceMap) => {
        this.graph?.destroy();

        this.renderDiagram(this.diagramContainer().nativeElement, traceMap);
    }, 500);

    constructor() {
        effect(() => {
            this.debounceUpdate(this.traceMap());
        });
    }
    renderDiagram(container: HTMLElement, data: EventTraceMap) {
        // Disables the built-in context menu
        InternalEvent.disableContextMenu(container);

        const nodeMap = new Map<string, Cell>();

        this.graph = getGraph(container);
        const hierarchicalLayout = new HierarchicalLayout(
            this.graph,
            DIRECTION.WEST,
            true,
        );

        this.graph.setHtmlLabels(true);

        this.graph.setPanning(false); // Use mouse right button for panning
        // Gets the default parent for inserting new cells. This
        // is normally the first child of the root (ie. layer 0).
        const parent = this.graph.getDefaultParent();
        this.graph.removeCells(null, true);

        // Adds cells to the model in a single step
        this.graph.batchUpdate(() => {
            data.nodes.forEach((node) => {
                const list =
                    Object.entries(node.info.evt.meta ?? {})?.length > 0
                        ? `<ul class="m-0 list-disc text-left">
                              ${Object.entries(node.info.evt.meta ?? {})
                                  .reverse()
                                  .map(
                                      ([key, value]: [
                                          key: string,
                                          value: any,
                                      ]) => {
                                          return `<li class="my-0 py-0"><strong>${startCase(
                                              key,
                                          )}</strong>: ${value}</li>`;
                                      },
                                  )}
                          </ul>`
                        : ``;
                const value = `
                    <section>
                        <div class="title-large-strong">
                            ${node.info.evt.name}
                        </div>
                        ${list}
                    </section>
                `;

                const width = node.info.evt.name.length * 14 + 20;
                const height =
                    Object.entries(node.info.evt.meta ?? {})?.length * 25 + 75;

                const vertex = this.graph.insertVertex({
                    id: node.id,
                    parent,
                    position: [10, 0],
                    size: [width, height],
                    value,
                    style: this.getDefaultVertexStyle(),
                });
                nodeMap.set(node.id, vertex);
            });

            data.links.forEach((edge) => {
                const source = nodeMap.get(edge.sourceEvent.id);
                const target = nodeMap.get(edge.targetEvent.id);

                this.graph.insertEdge({
                    parent,
                    source,
                    target,
                    style: this.getDefaultEdgeStyle(),
                });
            });
        });

        this.executeLayout(this.graph, hierarchicalLayout, null, null);

        this.graph.addListener(
            InternalEvent.CLICK,
            (sender: EventDiagramGraph, evt: EventObject) => {
                this.updateHighlightCells(evt);
            },
        );
    }

    private getDefaultEdgeStyle(): CellStyle {
        return {
            edgeStyle: 'orthogonalEdgeStyle',
            rounded: true,
            strokeColor: '#192129',
        };
    }
    private getHighlightedEdgeStyle(): CellStyle {
        return {
            edgeStyle: 'orthogonalEdgeStyle',
            rounded: true,
            strokeColor: '#0046e0',
        };
    }

    private getDefaultVertexStyle() {
        return {
            rounded: true,
            fontFamily: 'Sequel Sans',
            fillColor: '#fff',
            fontSize: 14,
            strokeColor: '#9fbbf7',
        };
    }
    private getHiglightedVertexStyle(): CellStyle {
        return {
            rounded: true,
            fontFamily: 'Sequel Sans',
            fillColor: '#0046e0',
            fontSize: 14,
            strokeColor: '#0046e0',
            fontColor: '#ffffff',
        };
    }

    private updateHighlightCells(evt: EventObject) {
        const currentCell: Cell = evt.getProperty('cell');

        if (!currentCell) return;

        const oldCells: Cell[] = this.graph.getCells(0, 0, 100000, 100000);

        oldCells.forEach((cell) => {
            if (cell.isVertex()) {
                cell.setStyle(this.getDefaultVertexStyle());
            }
            if (cell.isEdge()) {
                cell.setStyle(this.getDefaultEdgeStyle());
            }
        });

        this.selected.emit(currentCell.getId());

        currentCell.setStyle(this.getHiglightedVertexStyle());

        // currentCell.getEdges().forEach((edge: Cell) => {
        //     edge.target.setStyle(this.getHiglightedVertexStyle());
        //     edge.setStyle(this.getHighlightedEdgeStyle());
        // });

        this.graph.refresh();
    }

    executeLayout(graph, layout, change, post) {
        let v1 = null;
        graph.getDataModel().beginUpdate();
        try {
            if (change != null) {
                change();
            }
            layout.execute(graph.getDefaultParent(), v1);
        } catch (e) {
            throw e;
        } finally {
            // New API for animating graph layout results asynchronously
            const morph = new Morphing(graph);
            morph.addListener(InternalEvent.DONE, () => {
                graph.getDataModel().endUpdate();
                if (post != null) {
                    post();
                }
            });
            morph.startAnimation();
        }
    }
}

class EventDiagramGraph extends Graph {}

function getGraph(container) {
    const graph = new EventDiagramGraph(container);

    return graph;
}
